/**
 * TECHNICIAN HANDOFF — why this ticket was sent to the front office.
 *
 * Presentational and reusable (Command Center now; the FOH Workboard later):
 *
 *     TECHNICIAN HANDOFF
 *     Joseph · 2:37 PM
 *     Diagnosis complete — awaiting next decision
 *     "RF door module receives lock/unlock but is not supplying motor output."
 *     [Accept Ticket]            ← only while pending_advisor_accept
 *
 * The handoff is the latest MEANINGFUL handoff (a kick), never the accept
 * that followed it, so the reason and note survive acceptance. Identity is the
 * person's name; the role only when the name is unknown.
 */
import { format, isToday, parseISO } from 'date-fns';
import { ArrowRightCircle, MessageSquareQuote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { CustodyStatus } from '@/lib/tickets/lifecycle/types';

export interface TechnicianHandoffView {
  /** "Joseph" (or a role / "System" fallback). */
  actor: string;
  /** Secondary role text, shown only next to a real name. */
  actorRole?: string | null;
  at: string;
  /** Friendly reason label, never a raw code. */
  reason: string | null;
  notes: string | null;
}

export interface TechnicianHandoffCardProps {
  handoff: TechnicianHandoffView | null;
  custodyStatus?: CustodyStatus | null;
  /** Present only for someone who may accept; shown while pending_advisor_accept. */
  onAccept?: () => void;
  accepting?: boolean;
  title?: string;
}

function formatWhen(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  const d = parseISO(iso);
  return isToday(d) ? format(d, 'h:mm a') : format(d, 'MMM d, h:mm a');
}

export function TechnicianHandoffCard({
  handoff,
  custodyStatus,
  onAccept,
  accepting = false,
  title = 'Technician handoff',
}: TechnicianHandoffCardProps) {
  const awaitingAccept = custodyStatus === 'pending_advisor_accept';
  if (!handoff && !awaitingAccept) return null;
  return (
    <section
      data-testid="technician-handoff-card"
      className="rounded-lg border-2 border-primary/40 bg-primary/5 p-3 space-y-1.5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>
          {handoff ? (
            <>
              <div className="text-sm" data-testid="technician-handoff-who">
                <span className="font-medium">{handoff.actor}</span>
                {handoff.actorRole && <span className="text-muted-foreground"> ({handoff.actorRole})</span>}
                {formatWhen(handoff.at) && <span className="text-muted-foreground"> · {formatWhen(handoff.at)}</span>}
              </div>
              {handoff.reason && (
                <div className="text-sm font-semibold" data-testid="technician-handoff-reason">{handoff.reason}</div>
              )}
              {handoff.notes && (
                <blockquote
                  data-testid="technician-handoff-notes"
                  className="flex gap-1.5 text-sm italic text-foreground/90 whitespace-pre-wrap break-words"
                >
                  <MessageSquareQuote className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground not-italic" />
                  <span>“{handoff.notes}”</span>
                </blockquote>
              )}
            </>
          ) : (
            <div className="text-sm text-muted-foreground">Waiting for an advisor to accept this ticket.</div>
          )}
        </div>
        {awaitingAccept && onAccept && (
          <Button
            size="sm"
            onClick={onAccept}
            disabled={accepting}
            data-testid="technician-handoff-accept"
            className="gap-1 shrink-0"
          >
            <ArrowRightCircle className="h-3 w-3" />
            {accepting ? 'Accepting…' : 'Accept Ticket'}
          </Button>
        )}
      </div>
    </section>
  );
}
